// Shared URL → embed-iframe-src conversion.
//
// Two consumers:
//   · LiveFeed.SocialEmbed — for individual feed entries (Instagram
//     posts, tweets, Facebook posts, YouTube clips).
//   · CountingStream — for the editor-managed counting-hall stream
//     slot (Facebook live, YouTube live).
//
// Returns null if the URL doesn't match any known platform pattern.
// Callers fall back to a plain link in that case.

/** Convert a pasted URL to a value safe to drop into <iframe src>.
 *  Recognises:
 *    · Instagram: /p/<id>, /reel/<id>, /reels/<id>, /tv/<id>
 *    · YouTube: watch?v=<id>, /live/<id>, youtu.be/<id>, /shorts/<id>
 *    · Facebook video: /watch/?v=, /<page>/videos/<id>, fb.watch/<id>
 *    · X/Twitter: /<user>/status/<id>
 *  Returns null when none match — callers should render a link
 *  fallback rather than an iframe with an arbitrary src.
 *
 *  Pasted iframe widget src strings (e.g. already-formed plugin URLs)
 *  pass through unchanged when they're already on a known embed host. */
export function toFeedEmbedSrc(input: string): string | null {
  const url = input.trim();
  if (!url) return null;

  // ── Already-formed embed iframe src — pass through. ──
  if (
    url.startsWith('https://www.facebook.com/plugins/') ||
    url.startsWith('https://www.youtube.com/embed/') ||
    url.startsWith('https://www.instagram.com/p/') && url.endsWith('/embed/') ||
    url.startsWith('https://www.instagram.com/reel/') && url.endsWith('/embed/') ||
    url.startsWith('https://platform.twitter.com/embed/')
  ) {
    return url;
  }

  // ── Instagram posts / reels / IGTV ──
  // Match instagram.com/(p|reel|reels|tv)/<id>/...
  const ig = url.match(
    /instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/,
  );
  if (ig) {
    // The /reels/ plural form is a recent UX rename; the embed URL
    // still uses /reel/ (singular).
    const kind = ig[1] === 'reels' ? 'reel' : ig[1];
    return `https://www.instagram.com/${kind}/${ig[2]}/embed/`;
  }

  // ── YouTube ──
  // /watch?v=<id>, /live/<id>, /shorts/<id>, youtu.be/<id>
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?v=|live\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  );
  if (yt) {
    return `https://www.youtube.com/embed/${yt[1]}`;
  }

  // ── Facebook video ──
  // /<page>/videos/<id>, /watch/?v=<id>, fb.watch/<id>
  if (
    /facebook\.com\/.*\/videos?\//.test(url) ||
    /facebook\.com\/watch\/?\?v=/.test(url) ||
    /^https?:\/\/(www\.)?fb\.watch\//.test(url)
  ) {
    const params = new URLSearchParams({
      href: url,
      show_text: 'false',
      width: '560',
    });
    return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
  }

  // ── Facebook post (non-video) ──
  // /<page>/posts/<id>, /permalink.php?...
  if (
    /facebook\.com\/.+\/posts\//.test(url) ||
    /facebook\.com\/permalink\.php/.test(url)
  ) {
    const params = new URLSearchParams({
      href: url,
      show_text: 'true',
      width: '500',
    });
    return `https://www.facebook.com/plugins/post.php?${params.toString()}`;
  }

  // ── X / Twitter status ──
  // twitter.com/<user>/status/<id> or x.com/<user>/status/<id>
  const tw = url.match(
    /(?:twitter\.com|x\.com)\/[^/]+\/status\/(\d+)/,
  );
  if (tw) {
    return `https://platform.twitter.com/embed/Tweet.html?id=${tw[1]}`;
  }

  return null;
}

/** Display-friendly platform name for a URL — used for fallback link
 *  labels and aria. Returns "post" as a generic fallback. */
export function platformLabel(url: string): string {
  if (/instagram\.com/.test(url)) return 'Instagram';
  if (/youtube\.com|youtu\.be/.test(url)) return 'YouTube';
  if (/facebook\.com|fb\.watch/.test(url)) return 'Facebook';
  if (/twitter\.com|x\.com/.test(url)) return 'X';
  return 'post';
}
